import React, {forwardRef, useContext, useEffect, useRef} from 'react';
import {withErrorBoundary} from 'react-error-boundary';
import RelatedProductsTabbedView from 'features/TabbedView';
import useRelatedProductsTabbedView from 'features/TabbedView/hooks/useRelatedProductsTabbedView';
import useRecommendedStyles from 'hooks/queries/recommendations';
import useRecoBrands from 'hooks/queries/recommendationsBrands';
import useRelatedStyles from 'hooks/queries/relatedStyles';
import useBreakpoint from 'hooks/useBreakpoint';
import CurrentSelectedColorContext from 'product_details_page/Context/colorContext';
import ProductContext from 'product_details_page/Context/productContext';
import rollbar from 'src/vendor/rollbar';
import isAlgoliaRecommendations from 'utils/isAlgoliaRecommendations';
import isCanadianStyle from 'utils/isCanadianStyle';
import {fireRecommendationsLoadedEvent} from 'utils/metrics/pdpRelatedStyles';
import AlternativeProducts from './AlternativeProducts';
import ComplementProducts from './ComplementProducts';

const RelatedProducts = forwardRef(function RelatedProducts(_, similarRef) {
  const breakpoint = useBreakpoint();
  const {currentSelectedColor, singles} = useContext(
    CurrentSelectedColorContext
  );
  const {
    brand,
    id: styleId,
    isApparel,
    minQty,
    name,
    primaryCategoryId
  } = useContext(ProductContext);
  const firedAnalyticsEvent = useRef(false);
  const tabbedViewRef = useRef(null);

  const {
    data: relatedProducts,
    isError: isRelatedProductsError,
    isFetched: isRelatedProductsFetched,
    isRefetching: isRelatedProductsRefetching,
    isSuccess: isRelatedProductsSuccess
  } = useRelatedStyles(styleId, currentSelectedColor?.id, {
    enabled: !!currentSelectedColor?.id,
    keepPreviousData: true,
    placeholderData: {
      alternatives: [],
      complements: []
    }
  });

  const {
    data: recommendations,
    isFetched: isRecommendationsProductsFetched,
    isRefetching: isRecommendationsProductsRefetching,
    isSuccess: isRecommendationsProductsSuccess
  } = useRecommendedStyles(styleId, {
    enabled: !!currentSelectedColor?.id,
    keepPreviousData: false,
    placeholderData: {
      related_products: []
    },
    queryParams: {
      colorId: currentSelectedColor?.id,
      limit: 30
    }
  });

  const isCanadian = isCanadianStyle(name);
  const {
    data: recoBrandProducts,
    isFetched: isRecoBrandProductsFetched,
    isRefetching: isRecoBrandProductsRefetching
  } = useRecoBrands({
    enabled: !!currentSelectedColor?.id && !!brand && !isCanadian,
    keepPreviousData: false,
    placeholderData: {
      brand_products: []
    },
    queryParams: {
      brand,
      colorId: currentSelectedColor?.id,
      limit: 30,
      sortBy: 'rating', // can use 'category' as another option
      styleId
    }
  });

  useEffect(() => {
    if (
      isRelatedProductsSuccess &&
      isRelatedProductsFetched &&
      !firedAnalyticsEvent.current
    ) {
      firedAnalyticsEvent.current = true;
      const hasRecommendations =
        relatedProducts.alternatives.length > 0 ||
        relatedProducts.complements.length > 0;
      fireRecommendationsLoadedEvent(styleId, hasRecommendations);
    }
  }, [
    isRelatedProductsSuccess,
    isRelatedProductsFetched,
    relatedProducts,
    styleId
  ]);

  const alternativeProducts =
    isAlgoliaRecommendations() && isRecommendationsProductsSuccess
      ? recommendations?.related_products.filter(
          (item) => item?.primary_category_id === primaryCategoryId
        )
      : relatedProducts?.alternatives;

  const {
    hasAnyData,
    relatedProductsActiveTab,
    setRelatedProductsActiveTab,
    shouldTabbedViewRender,
    tabbedViewIsLoading,
    tabbedViewProducts,
    tabs
  } = useRelatedProductsTabbedView({
    alternativeProducts,
    brand,
    breakpoint,
    isApparel,
    isNoMinimumSelected: singles,
    isRecoBrandProductsFetched,
    isRecoBrandProductsRefetching,
    isRecommendationsProductsFetched,
    isRecommendationsProductsRefetching,
    isRelatedProductsFetched,
    isRelatedProductsRefetching,
    minQty,
    primaryCategoryId,
    recoBrandProducts,
    recommendations,
    relatedProducts
  });

  if (isRelatedProductsError) {
    return null;
  }

  return (
    <>
      {shouldTabbedViewRender && (
        <RelatedProductsTabbedView
          relatedProductsRef={tabbedViewRef}
          tabs={tabs}
          activeTab={relatedProductsActiveTab}
          onTabChange={setRelatedProductsActiveTab}
          isLoading={tabbedViewIsLoading}
          products={tabbedViewProducts}
          hasAnyData={hasAnyData}
        />
      )}
      {!shouldTabbedViewRender && alternativeProducts?.length > 0 && (
        <AlternativeProducts
          alternativeProducts={alternativeProducts.slice(0, 4)}
          similarRef={similarRef}
        />
      )}
      {!shouldTabbedViewRender && relatedProducts?.complements.length > 0 && (
        <ComplementProducts complementProducts={relatedProducts?.complements} />
      )}
    </>
  );
});

const RelatedProductsWithErrorBoundary = withErrorBoundary(RelatedProducts, {
  fallback: <div>Something went wrong!</div>,
  onError: (error, componentStack) => {
    rollbar.error(error, componentStack);
  }
});

export default RelatedProductsWithErrorBoundary;
